import { useMemo } from 'react';
import { X } from 'lucide-react';
import { useT } from '../../i18n';
import { parseJSON } from '../../components/ProjectForm';
import type { Project } from '../../types';
import { PROJECT_ROLES, effectiveProjectRole, getProjectRole } from './projectRoles';

// [Proposition: roles-projet] Who does what on a project: each person with
// their role, the role's standard explanation and, when filled in, the
// project-specific précision. Used on the proposals' Roles tab and opened from
// the project's Execution tab ("Équipe et rôles").

interface TeamMember { name: string; roleId: string; precision: string }

export function projectTeam(project: Project): TeamMember[] {
  const ownership = parseJSON<any>(project.ownership, {});
  const sponsor = String(ownership.sponsor || '').trim();
  const allocations = parseJSON<any>(project.scoping, {}).allocations;
  const team: TeamMember[] = (Array.isArray(allocations) ? allocations : [])
    .filter((a: any) => String(a?.name || '').trim())
    .map((a: any) => ({
      name: String(a.name).trim(),
      roleId: effectiveProjectRole(String(a.name), a.project_role, sponsor),
      precision: String(a.role || '').trim(),
    }));
  // The sponsor is defined by the Sponsor field, even when missing from the team table.
  if (sponsor && !team.some(m => m.roleId === 'sponsor')) team.push({ name: sponsor, roleId: 'sponsor', precision: '' });
  // So is the PMO accompanist, by its own field.
  const accompanist = String(ownership.pmo_accompanist || '').trim();
  if (accompanist) {
    const member = team.find(m => m.name.toLowerCase() === accompanist.toLowerCase());
    if (member) member.roleId = 'accompanist';
    else team.push({ name: accompanist, roleId: 'accompanist', precision: '' });
  }
  // Most involved first (role order), people without a role last.
  const rank = (id: string) => { const i = PROJECT_ROLES.findIndex(r => r.id === id); return i < 0 ? PROJECT_ROLES.length : i; };
  return team.sort((a, b) => rank(a.roleId) - rank(b.roleId) || a.name.localeCompare(b.name));
}

/** The project's team with each person's role and précision. */
export function ProjectTeamRoles({ project }: { project: Project }) {
  const { t } = useT();
  const team = useMemo(() => projectTeam(project), [project]);
  if (team.length === 0) return <p className="text-sm text-ink-3">{t('roles.teamEmpty')}</p>;
  return (
    <div className="roles-team">
      {team.map(m => {
        const role = getProjectRole(m.roleId);
        return (
          <div key={m.name} className="roles-team-row">
            <div className="roles-team-name">{m.name}</div>
            <div className="roles-team-role">
              <span className="badge">{role ? t(role.labelKey) : t('roles.none')}</span>
              <div className="roles-team-desc">
                <span className="roles-team-tag">{t('roles.standardDesc')}</span>
                {role ? t(role.descKey) : t('roles.none.desc')}
              </div>
              {m.precision && (
                <div className="roles-team-precision">
                  <span className="roles-team-tag">{t('roles.projectPrecision')}</span>
                  {m.precision}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Side panel opened from the Execution tab: the team and their roles. */
export function ProjectTeamRolesPanel({ project, onClose }: { project: Project; onClose: () => void }) {
  const { t } = useT();
  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide" onClick={e => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div className="panel-slide-title">{t('roles.teamTitle')} · {project.name}</div>
          <button className="panel-slide-close" onClick={onClose} title={t('common.close')}>
            <X size={16} />
          </button>
        </div>
        <div className="panel-slide-body">
          <ProjectTeamRoles project={project} />
        </div>
      </div>
    </div>
  );
}
